import {
  ButtonBase,
  createTheme,
  Grid,
  InputAdornment,
  Paper,
  TextField,
  ThemeProvider,
  Typography,
  Checkbox,
  Menu,
  Chip,
  Stack,
  MenuItem,
  ListItemText,
  FormControl,
  InputLabel,
  Select,
} from "@mui/material";
import { GridSearchIcon } from "@mui/x-data-grid";
import "./HeaderBar.css";
import { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { Tune } from "@mui/icons-material";
import TopSection from "../../CommonComponents/TopSection";
import { useLocation } from "react-router-dom";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import { fetchFilters, updateCount } from "./services/headerBarServices";
import { useDispatch } from "react-redux";
import { openDrawer } from "../../redux/filterDrawerSlice";


const searchTypes = ["Shipment No", "Item", "Delivery No", "Order No"];
const capitalize = (s = "") => (s ? s[0].toUpperCase() + s.slice(1).toLowerCase() : "");

const getChipColor = (key) => {
  const colors = {
    "Delayed": { color: "#f79900" }, "Early": { color: "#bc6bd9" }, "On Time": { color: "#268000" },
    "Detention Breach": { color: "#ff560e" }, "Delete": { color: "#ff560e" }, "Pre Detention Breach": { color: "#ff560e" },
    "Demurrage Breach": { color: "#ff560e" }, "Late": { color: "#ff560e" }, "Untracked": { color: "#f40000" },
    "PTA Unknown": { color: "#e5813e" }, "Stockout": { color: "#f40000" }, "Batch Expired": { color: "#ff560e" },
    "Over Stock": { color: "#bc6bd9" }, "Low Stock": { color: "#f79900" }, "Batch Expiring": { color: "#e5813e" },
    "On Track": { color: "#268000" }, "No Exception": { color: "#000" }, "Planned Shipment": { color: "#d7b85b" },
    "Shipped": { color: "#b59e74" }, "In Transit": { color: "#f79900" }, "Delivered": { color: "#6ace7f" },
    "Closed": { color: "#f40000" }, "In Transit Cancelled": { color: "#f79900" }, "Received": { color: "#6ac9ce" },
    "Past Due": { color: "#fbc02d" }, "Unconfirmed": { color: "#bc6bd9" }, "Overstock": { color: "#bc6bd9" },
  };
  return colors[key] || {};
};

const mapOrderTypeForApi = (value) => {
  if (!value) return null;

  const normalized = value.toLowerCase().replace(/\s+/g, '');

  if (normalized === 'stocktransferpo') return 'stockTransferPO';
  if (normalized === 'standardpo') return 'standardPO';

  return value; // fallback
};


export const HeaderBar = ({
  title,
  count,
  onSearch,
  isSearchAndFilter = true,
  exceptions,
  states,
  onFilterSelect = () => { },
  orderType,
  onOrderTypeChange = () => { },
}) => {
  const theme = createTheme({
    components: {
      MuiTypography: {
        defaultProps: { fontSize: 13, height: "100%" },
      },
    },
  });
  const dispatch = useDispatch();
  // page identity (namespaced storage)
  const location = useLocation();
  const isDashboard = location.pathname === "/ct-dashboard";
  const pathSegments = location.pathname.split("/");
  let category = pathSegments[2] || "";
  let subCategory = pathSegments[3] || "";
  if (pathSegments[2] === "inventory") {
    category = pathSegments[2];
    subCategory = "Shipment";
  }
  const isDemandSalesOrder = category === "demand" && subCategory === "order";
  const getSearchPayload = () => {
    if (!isDemandSalesOrder || !searchValue) return undefined;

    return {
      type: "Order_No",
      value: searchValue,
    };
  };
  const isDev = process.env.REACT_APP_ENVT === "DEV";
  const pageKey = category !== 'inventory' ? `${category || "home"}_${subCategory || "default"}` : 'inventory';
  const storageKey = `filters_${pageKey}`;
  const priorityKey = `priority_${pageKey}`;
  const columnFiltersKey = `columnFilters_${pageKey}`;

  const handleOrderTypeChange = (event) => {
    const rawValue = event.target.value;
    const mappedValue = mapOrderTypeForApi(rawValue);
    onOrderTypeChange(mappedValue);
    orderTypeRef.current = mappedValue;
  };

  const [searchValue, setSearchValue] = useState("");
  const [anchorElExceptions, setAnchorElExceptions] = useState(null);
  const [anchorElStates, setAnchorElStates] = useState(null);
  const [exceptionOptions, setExceptionOptions] = useState({});
  const [stateOptions, setStateOptions] = useState({});
  const [selectedExceptions, setSelectedExceptions] = useState(new Set());
  const [selectedStates, setSelectedStates] = useState(new Set());
  const [priorityType, setPriorityType] = useState(null);
  const [pageSearchValue, setPageSearchValue] = useState("");
  const [emitPageSearch, setEmitPageSearch] = useState(() => () => { });

  // restore / load flags
  const [isRestored, setIsRestored] = useState(false);
  const [filtersLoaded, setFiltersLoaded] = useState(false);
  const initialLoadRef = useRef(true);
  const exceptionOptionsRef = useRef(exceptionOptions);
  const stateOptionsRef = useRef(stateOptions);
  const selectedExceptionsRef = useRef(selectedExceptions);
  const selectedStatesRef = useRef(selectedStates);
  const priorityTypeRef = useRef(priorityType);
  const prevStorageKeyRef = useRef(null);
  const prevPriorityKeyRef = useRef(null);
  const prevColumnFiltersKeyRef = useRef(null);
  const refreshRunningRef = useRef(false);
  const needRunAgainRef = useRef(false);
  const lastColumnFiltersRef = useRef(null);
  const refreshDebounceTimerRef = useRef(null);
  const orderTypeRef = useRef(orderType);
  const prevOrderTypeRef = useRef(orderType);
  const searchTriggeredRef = useRef(false);

  useEffect(() => { exceptionOptionsRef.current = exceptionOptions; }, [exceptionOptions]);
  useEffect(() => { stateOptionsRef.current = stateOptions; }, [stateOptions]);
  useEffect(() => { selectedExceptionsRef.current = selectedExceptions; }, [selectedExceptions]);
  useEffect(() => { selectedStatesRef.current = selectedStates; }, [selectedStates]);
  useEffect(() => { priorityTypeRef.current = priorityType; }, [priorityType]);
  useEffect(() => {
    prevOrderTypeRef.current = orderTypeRef.current;
    orderTypeRef.current = orderType;
  }, [orderType]);

  useEffect(() => {
    try {
      const saved = localStorage.getItem(priorityKey);
      if (saved) setPriorityType(saved);
    } catch (e) { /* ignore */ }
  }, [priorityKey]);

  useEffect(() => {
    try {
      if (priorityType) localStorage.setItem(priorityKey, priorityType);
      else localStorage.removeItem(priorityKey);
    } catch (e) { /* ignore */ }
  }, [priorityType, priorityKey]);

  const setPriority = useCallback((type) => {
    setPriorityType(type);
    priorityTypeRef.current = type;
    try { localStorage.setItem(priorityKey, type || ""); } catch (e) { }
  }, [priorityKey]);

  const getMappedExceptions = useCallback((opts) =>
    [...selectedExceptions].map(k => ({ isActive: true, detailName: k, count: opts[k] ?? 0 })), [selectedExceptions]);
  const getMappedStates = useCallback((opts) =>
    [...selectedStates].map(k => ({ isActive: true, detailName: k, count: opts[k] ?? 0 })), [selectedStates]);

  const loadFilters = useCallback(async () => {
    if (!category || !subCategory) return;
    try {
      const [exceptionsResp, statesResp] = await Promise.all([
        fetchFilters(category, subCategory, "Exceptions", orderTypeRef.current),
        fetchFilters(category, subCategory, "States", orderTypeRef.current),
      ]);
      const exMap = {};
      const stMap = {};
      (exceptionsResp || []).forEach(({ detailName, count }) => { exMap[detailName] = count ?? 0; });
      (statesResp || []).forEach(({ detailName, count }) => { stMap[detailName] = count ?? 0; });

      setExceptionOptions(exMap);
      setStateOptions(stMap);
      setFiltersLoaded(true);
    } catch (err) {
      console.error("HeaderBar.loadFilters failed", err);
    }
  }, [category, subCategory]);

  const mergeAndZeroMissing = useCallback((prevMap = {}, newMap = {}) => {
    const out = {};
    const keys = new Set([...Object.keys(prevMap || {}), ...Object.keys(newMap || {})]);
    keys.forEach(k => { out[k] = newMap.hasOwnProperty(k) ? (newMap[k] ?? 0) : 0; });
    return out;
  }, []);

  const shouldSendOrderType =
    category === "supply" && subCategory === "order";

  const isOrderTypeChangeOnly =
    shouldSendOrderType &&
    prevOrderTypeRef.current !== orderTypeRef.current;

  const getOrderTypeForApi = () =>
    shouldSendOrderType ? mapOrderTypeForApi(orderTypeRef.current) : undefined;

  const refreshCounts = useCallback(async () => {
    if (!category || !subCategory) return;
    if (refreshRunningRef.current) {
      needRunAgainRef.current = true;
      return;
    }
    refreshRunningRef.current = true;
    needRunAgainRef.current = false;

    const currentExceptionOptions = exceptionOptionsRef.current;
    const currentStateOptions = stateOptionsRef.current;
    const selectedExceptionPayload = [...selectedExceptionsRef.current].map(d => ({ isActive: true, detailName: d, count: currentExceptionOptions[d] ?? 0 }));
    const selectedStatePayload = [...selectedStatesRef.current].map(d => ({ isActive: true, detailName: d, count: currentStateOptions[d] ?? 0 }));
    let columnFiltersData = {};
    try {
      const raw = localStorage.getItem(columnFiltersKey);
      columnFiltersData = raw ? JSON.parse(raw) : null;
    } catch (e) {
      columnFiltersData = null;
    }

    const hasColumnFilters = columnFiltersData !== null;
    const currentPriority = priorityTypeRef.current;
    const baseFilterPayload = {
      ...(isDemandSalesOrder && searchTriggeredRef.current
        ? {
          search: getSearchPayload(),
          columnFilters: {},
        }
        : {
          [columnFiltersKey]: columnFiltersData,
        }
      ),
    };
    if (!currentPriority && !hasColumnFilters && !isOrderTypeChangeOnly) {
      refreshRunningRef.current = false;
      if (needRunAgainRef.current) {
        needRunAgainRef.current = false;
        setTimeout(() => refreshCounts(), 50);
      }
      return;
    }

    try {
      let updatedExceptionOptions = { ...currentExceptionOptions };
      let updatedStateOptions = { ...currentStateOptions };

      if (hasColumnFilters) {
        searchTriggeredRef.current = false;

        try {
          const payloadStates = {
            category: capitalize(category),
            subCategory: capitalize(subCategory),
            type: "Exceptions",
            selectedExceptions: selectedExceptionPayload,
            selectedStates: selectedStatePayload,
            ...baseFilterPayload,
          };
          const respStates = await updateCount(payloadStates, getOrderTypeForApi());
          const stateMap = {};
          (respStates || []).forEach(({ detailName, count }) => stateMap[detailName] = count ?? 0);

          updatedStateOptions = mergeAndZeroMissing(currentStateOptions, stateMap);
        } catch (err) {
          console.error("HeaderBar.refreshCounts (states) failed", err);
        }

        try {
          const payloadExceptions = {
            category: capitalize(category),
            subCategory: capitalize(subCategory),
            type: "States",
            selectedExceptions: selectedExceptionPayload,
            selectedStates: selectedStatePayload,
            ...baseFilterPayload,
          };

          const respExceptions = await updateCount(payloadExceptions, getOrderTypeForApi());
          const exMap = {};
          (respExceptions || []).forEach(({ detailName, count }) => exMap[detailName] = count ?? 0);

          updatedExceptionOptions = mergeAndZeroMissing(currentExceptionOptions, exMap);
        } catch (err) {
          console.error("HeaderBar.refreshCounts (exceptions) failed", err);
        }
        setExceptionOptions(updatedExceptionOptions);
        setStateOptions(updatedStateOptions);
      } else {

        if (currentPriority) {
          try {
            const payload =
              isDemandSalesOrder && searchTriggeredRef.current
                ? {
                  category: "Demand",
                  subCategory: "Order",
                  type: "Exceptions",
                  selectedExceptions: [],
                  selectedStates: [],
                  search: {
                    type: "Order_No",
                    value: searchValue,
                  },
                  columnFilters: {},
                }
                : {
                  category: capitalize(category),
                  subCategory: capitalize(subCategory),
                  type: currentPriority,
                  selectedExceptions: selectedExceptionPayload,
                  selectedStates: selectedStatePayload,
                  ...baseFilterPayload,
                };
            const response = await updateCount(payload, getOrderTypeForApi());
            searchTriggeredRef.current = false;
            const map = {};
            (response || []).forEach(({ detailName, count }) => { map[detailName] = count ?? 0; });

            if (currentPriority === "Exceptions") {
              // server response contains states counts -> zero-missing for states
              setStateOptions(prev => mergeAndZeroMissing(prev, map));
            } else {
              // server response contains exceptions counts -> zero-missing for exceptions
              setExceptionOptions(prev => mergeAndZeroMissing(prev, map));
            }
          } catch (err) {
            console.error("HeaderBar.refreshCounts failed", err);
          }
        }
      }
    } catch (err) {
      console.error("HeaderBar.refreshCounts failed", err);
    } finally {
      refreshRunningRef.current = false;

      if (needRunAgainRef.current) {
        needRunAgainRef.current = false;
        setTimeout(() => refreshCounts(), 50);
      }
    }
  }, [category, subCategory, columnFiltersKey, mergeAndZeroMissing, searchValue, orderType,          // IMPORTANT
    shouldSendOrderType, isDemandSalesOrder]); // Added searchValue dependency

  useEffect(() => {
    if (!isRestored || !filtersLoaded) return;
    refreshCounts();
  }, [orderType, isRestored, filtersLoaded, refreshCounts]);

  useEffect(() => {

    if (prevStorageKeyRef.current && prevStorageKeyRef.current !== storageKey) {
      console.log(`🔑 Page Transition: Old Key: ${prevStorageKeyRef.current} -> New Key: ${storageKey}`);
    }

    const prevKeys = prevColumnFiltersKeyRef.current;

    if (prevKeys && prevKeys !== columnFiltersKey) {
      try {
        if (localStorage.getItem(prevKeys) !== null) {
          localStorage.removeItem(prevKeys);

        }
      } catch (err) {

      }
    }


    prevStorageKeyRef.current = storageKey;
    prevPriorityKeyRef.current = priorityKey;
    prevColumnFiltersKeyRef.current = columnFiltersKey;


    setIsRestored(false);
    setFiltersLoaded(false);
    initialLoadRef.current = true;
    setSelectedExceptions(new Set());
    setSelectedStates(new Set());
    setExceptionOptions({});
    setStateOptions({});
    setSearchValue("");
    setPageSearchValue("");


    setTimeout(() => {
      if (searchValue) {
        emitData("");
      }
    }, 50);



    try {
      const saved = localStorage.getItem(storageKey);
      if (saved) {
        const parsed = JSON.parse(saved);
        const restoredExceptions = parsed.selectedExceptions || [];
        const restoredStates = parsed.selectedStates || [];
        setSelectedExceptions(new Set(restoredExceptions.map(f => f.detailName)));
        setSelectedStates(new Set(restoredStates.map(f => f.detailName)));

        const exRestore = {};
        const stRestore = {};
        (restoredExceptions || []).forEach(f => { exRestore[f.detailName] = f.count ?? 0; });
        (restoredStates || []).forEach(f => { stRestore[f.detailName] = f.count ?? 0; });
        setExceptionOptions(exRestore);
        setStateOptions(stRestore);
      }
    } catch (err) {
      console.warn("HeaderBar.restore failed", err);
    } finally {
      const t = setTimeout(() => setIsRestored(true), 50);
      return () => clearTimeout(t);
    }
  }, [location.pathname, storageKey, priorityKey, columnFiltersKey]); // Added all keys as dependencies


  useEffect(() => {
    if (!category || !subCategory) return;
    setFiltersLoaded(false);
    loadFilters();
  }, [category, subCategory, loadFilters]);


  useEffect(() => {

    if (!isRestored || !filtersLoaded || !storageKey || initialLoadRef.current) return;
    try {
      const mappedExceptions = getMappedExceptions(exceptionOptions);
      const mappedStates = getMappedStates(stateOptions);
      localStorage.setItem(storageKey, JSON.stringify({ selectedExceptions: mappedExceptions, selectedStates: mappedStates }));
    } catch (err) {
      console.error("HeaderBar: persist failed", err);
    }
  }, [selectedExceptions, selectedStates, exceptionOptions, stateOptions, isRestored, filtersLoaded, storageKey, getMappedExceptions, getMappedStates]);


  useEffect(() => {
    if (!isRestored || !filtersLoaded) return;
    const hasFilters = selectedExceptions.size > 0 || selectedStates.size > 0;


    const t = setTimeout(() => {
      if (hasFilters && priorityType) {
        refreshCounts();
      } else if (!hasFilters && priorityType == null) {
        loadFilters();
      }
    }, 0);

    return () => clearTimeout(t);
  }, [selectedExceptions, selectedStates, priorityType, isRestored, filtersLoaded, refreshCounts, loadFilters]);


  useEffect(() => {
    if (!isRestored || !filtersLoaded) return;
    if (initialLoadRef.current) {
      initialLoadRef.current = false;
      onFilterSelect({ selectedExceptions: getMappedExceptions(exceptionOptions), selectedStates: getMappedStates(stateOptions) });
      return;
    }
    const id = setTimeout(() => {
      onFilterSelect({ selectedExceptions: getMappedExceptions(exceptionOptions), selectedStates: getMappedStates(stateOptions) });
    }, 120);
    return () => clearTimeout(id);
  }, [exceptionOptions, stateOptions, selectedExceptions, selectedStates, isRestored, filtersLoaded, getMappedExceptions, getMappedStates, onFilterSelect]);


  useEffect(() => {
    if (!category || !subCategory) return;
    const eventName = `columnFiltersChanged_${pageKey}`;
    console.log('eventName', eventName);
    const handler = () => {

      try {
        const raw = localStorage.getItem(columnFiltersKey);
        const columnFilters = raw ? JSON.parse(raw) : null;
        const serialized = columnFilters ? JSON.stringify(columnFilters) : null;
        console.log("HeaderBar: columnFiltersChanged event detected", serialized);
        if (serialized === lastColumnFiltersRef.current) return;
        lastColumnFiltersRef.current = serialized;

        if (refreshDebounceTimerRef.current) clearTimeout(refreshDebounceTimerRef.current);
        refreshDebounceTimerRef.current = setTimeout(() => {
          if (!isRestored || !filtersLoaded) {
            needRunAgainRef.current = true;
            return;
          }
          refreshCounts();
        }, 150);
      } catch (err) {
        console.warn("HeaderBar: failed to process columnFiltersChanged event", err);
      }
    };
    window.addEventListener(eventName, handler);
    return () => {
      if (refreshDebounceTimerRef.current) clearTimeout(refreshDebounceTimerRef.current);
      window.removeEventListener(eventName, handler);
    };
  }, [category, subCategory, pageKey, isRestored, filtersLoaded, refreshCounts, columnFiltersKey]);

  const getSearchKey = () => {
    if (category === "inventory") return "item";
    if (subCategory === "shipment") return "Shipment No";
    if (subCategory === "order") return "Order No";
    if (subCategory === "delivery") return "Delivery No";


    return null;
  };

  const emitData = (value) => {
    searchTriggeredRef.current = true;
    const searchKey = getSearchKey();

    onSearch({ type: searchKey, value });

    const raw = localStorage.getItem(columnFiltersKey);
    let filters = raw ? JSON.parse(raw) : {};

    let activeKey;
    let inactiveKey;

    if (subCategory === "delivery") {
      activeKey = "deliveryNo";
      inactiveKey = "item";
    } else if (subCategory === "order") {
      activeKey = "orderNo";
      inactiveKey = "item";
    } else {
      activeKey = category === "inventory" ? "item" : "shipmentNo";
      inactiveKey = category === "inventory" ? "shipmentNo" : "item";
    }

    delete filters[inactiveKey];

    if (value) {
      filters[activeKey] = {
        filterType: "set",
        values: [value],
      };
    } else {
      delete filters[activeKey];
    }

    localStorage.setItem(columnFiltersKey, JSON.stringify(filters));

    if (!(isDemandSalesOrder && searchTriggeredRef.current)) {
      window.dispatchEvent(
        new CustomEvent(`columnFiltersChanged_${pageKey}`)
      );
    }

    refreshCounts();
  };

  const isPurchaseOrders = category === "supply" && subCategory === "order";
  if (subCategory === "currency-conversion") isSearchAndFilter = false;

  const exceptionEntries = useMemo(() => Object.entries(exceptionOptions), [exceptionOptions]);
  const stateEntries = useMemo(() => Object.entries(stateOptions), [stateOptions]);

  const breadcrumbArray = (() => {
    if (category === "supply") {
      if (subCategory === "order") return ["Supply", "Purchase Orders"];
      if (subCategory === "shipment") return ["Supply", "Inbound Shipments"];
      if (subCategory === "delivery") return ["Supply", "Inbound Deliveries"];
      return ["Supply"];
    }
    if (category === "demand") {
      if (subCategory === "order") return ["Demand", "Sales Orders"];
      if (subCategory === "shipment") return ["Demand", "Outbound Shipments"];
      if (subCategory === "delivery") return ["Demand", "Outbound Deliveries"];
      return ["Demand"];
    }
    if (subCategory === "currency-conversion") {
      return ["Master Data", "Currency Conversion"];
    }
    if (category === "inventory") return ["Inventory", "Inventory List"];
    return [capitalize(category) || "Home"];
  })();


  return (
    <ThemeProvider theme={theme}>
      <Grid container size={{ xs: 12 }} sx={{ paddingBottom: "6px", alignItems: "center", marginBottom: "-15px" }}>
        <Grid container justifyContent="space-between" alignItems="center" sx={{ width: "100%", padding: "0 6px", marginTop: "-5px" }}>
          <Grid item xs={12}>
            <TopSection breadcrumbArray={breadcrumbArray} hideHeader headerName="" hideTitle showIcons count={count} />
          </Grid>
        </Grid>
      </Grid>

      {isSearchAndFilter && (
        <Grid container className="header-wrapper" alignItems="center">
          <Grid item xs={isPurchaseOrders ? 3.5 : 4}>
            <Grid container sx={{ margin: 0, height: "100%", width: "100%", gap: 2 }} spacing={2}>
              <Grid style={{ paddingLeft: "5px", paddingTop: "15px" }}>
                <Grid container rowSpacing={1}>
                  <Grid>
                    <Tune
                      fontSize="small"
                      className="tune-icon"
                      onClick={() => {
                        if (isDev && !isDashboard) {
                          dispatch(openDrawer());
                        }
                      }}
                      style={{
                        cursor: isDev ? "pointer" : "default",
                        opacity: isDev ? 1 : 0.4,
                        pointerEvents: isDev ? "auto" : "none",
                      }}
                    />
                  </Grid>
                  <Grid>
                    <TextField
                      label={process.env.REACT_APP_ENVT === "DEV" ? "Global Search" : "Search"}
                      variant="outlined"
                      size="small"
                      value={searchValue}
                      onChange={(e) => {
                        const value = e.target.value.replace(/\s+/g, "");
                        setSearchValue(value);
                      }}
                      onKeyUp={(e) => e.key === "Enter" && emitData(searchValue)}
                      autoComplete="off"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <ButtonBase onClick={() => emitData(searchValue)}>
                              <GridSearchIcon fontSize="small" />
                            </ButtonBase>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                </Grid>
              </Grid>

              <Grid item xs={7} paddingTop={2}>
                <Grid container alignItems="center" spacing={2} sx={{ flexWrap: "nowrap" }}>
                  {process.env.REACT_APP_ENVT === 'DEV' && (<Grid item>
                    <TextField
                      label="Page Search"
                      variant="outlined"
                      size="small"
                      value={pageSearchValue}
                      onChange={(e) => setPageSearchValue(e.target.value)}
                      onKeyUp={(e) => e.key === "Enter" && emitPageSearch(pageSearchValue)}
                      autoComplete="off"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <ButtonBase onClick={() => emitPageSearch(pageSearchValue)}>
                              <GridSearchIcon fontSize="small" />
                            </ButtonBase>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>)}

                  {isPurchaseOrders && (
                    <Grid item xs>
                      <FormControl fullWidth size="small">
                        <InputLabel id="order-type-label" sx={{ pr: 2 }}>Order Type</InputLabel>
                        <Select
                          labelId="order-type-label"
                          id="order-type-select"
                          value={orderType}
                          label="Order Type"
                          onChange={handleOrderTypeChange}
                          sx={{ fontSize: 15, minWidth: 160 }}
                        >
                          <MenuItem value="standardPO"><Typography fontSize={13}>Standard PO</Typography></MenuItem>
                          <MenuItem value="stockTransferPO"><Typography fontSize={13}>Stock Transfer PO</Typography></MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>)}
                </Grid>
              </Grid>

              {subCategory !== "currency-conversion" && <Grid size={{ xs: 1 }} sx={{ borderRight: "1.5px solid #cdcdcd" }} />}
            </Grid>
          </Grid>
          <Grid
            item
            sx={{
              borderRight: "1.5px solid #cdcdcd",
              height: "60px",
              mx: 2,
            }}
          />

          {subCategory !== "currency-conversion" && (
            <Grid item xs={isPurchaseOrders ? 6.5 : 8}>
              <Stack direction="column" spacing={1} sx={{ p: 0.5, mt: category === "inventory" ? 2 : 0 }}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <ButtonBase id="exceptions" onClick={(e) => setAnchorElExceptions(e.currentTarget)} sx={{ minWidth: 100 }}>
                    <Paper variant="outlined" sx={{ width: 80, px: 1, py: 0.5, background: "black", color: "white", borderWidth: 1, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                      <Typography variant="body2">Exceptions</Typography>
                      <NavigateNextIcon fontSize="small" />
                    </Paper>
                  </ButtonBase>

                  <Stack direction="row" spacing={0.5} flexWrap="wrap">
                    {[...selectedExceptions].map(key => {
                      const countVal = exceptionOptions[key] ?? 0;
                      const chipColor = getChipColor(key);
                      return <Chip key={key} onDelete={() => {
                        setSelectedExceptions(prev => {
                          const next = new Set(prev);
                          next.delete(key);
                          if (next.size === 0 && selectedStates.size > 0) setPriority("States");
                          if (next.size === 0 && selectedStates.size === 0) setPriority(null);
                          setTimeout(() => { if (isRestored && filtersLoaded) refreshCounts(); }, 0);
                          return next;
                        });
                      }} size="small" variant="outlined" label={<span style={{ fontWeight: 600, ...chipColor }}>{key} ({countVal})</span>} sx={{ mx: 0.5 }} />;
                    })}
                  </Stack>

                  <Menu id="exceptions-menu" anchorEl={anchorElExceptions} open={Boolean(anchorElExceptions)} onClose={() => setAnchorElExceptions(null)} anchorOrigin={{ vertical: "center", horizontal: "right" }} transformOrigin={{ vertical: "center", horizontal: "left" }}>
                    {exceptionEntries.map(([key, value]) => (
                      <MenuItem key={key} onClick={() => {
                        setSelectedExceptions(prev => {
                          const next = new Set(prev);
                          if (next.has(key)) next.delete(key); else next.add(key);
                          if (next.size > 0 && (!priorityType || selectedStates.size === 0)) setPriority("Exceptions");
                          if (next.size === 0 && selectedStates.size > 0) setPriority("States");
                          if (next.size === 0 && selectedStates.size === 0) setPriority(null);
                          setTimeout(() => { if (isRestored && filtersLoaded) refreshCounts(); }, 0);
                          return next;
                        });
                      }}>
                        <Checkbox checked={selectedExceptions.has(key)} size="small" />
                        <ListItemText primary={<span style={getChipColor(key)}>{`${key} (${value})`}</span>} />
                      </MenuItem>
                    ))}
                  </Menu>
                </Stack>

                {category !== "inventory" && (
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <ButtonBase id="states" onClick={(e) => setAnchorElStates(e.currentTarget)} sx={{ minWidth: 100 }}>
                      <Paper variant="outlined" sx={{ width: 80, px: 1, py: 0.5, background: "black", color: "white", borderWidth: 1, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                        <Typography variant="body2">States</Typography>
                        <NavigateNextIcon fontSize="small" />
                      </Paper>
                    </ButtonBase>

                    <Stack direction="row" spacing={0.5} flexWrap="wrap">
                      {[...selectedStates].map(key => {
                        const countVal = stateOptions[key] ?? 0;
                        const chipColor = getChipColor(key);
                        return <Chip key={key} label={<span style={{ fontWeight: 600, ...chipColor }}>{key} ({countVal})</span>} onDelete={() => {
                          setSelectedStates(prev => {
                            const next = new Set(prev);
                            next.delete(key);
                            if (next.size === 0 && selectedExceptions.size > 0) setPriority("Exceptions");
                            if (next.size === 0 && selectedExceptions.size === 0) setPriority(null);
                            setTimeout(() => { if (isRestored && filtersLoaded) refreshCounts(); }, 0);
                            return next;
                          });
                        }} size="small" variant="outlined" />;
                      })}
                    </Stack>

                    <Menu anchorEl={anchorElStates} open={Boolean(anchorElStates)} onClose={() => setAnchorElStates(null)} anchorOrigin={{ vertical: "center", horizontal: "right" }} transformOrigin={{ vertical: "center", horizontal: "left" }}>
                      {stateEntries.map(([key, value]) => (
                        <MenuItem key={key} onClick={() => {
                          setSelectedStates(prev => {
                            const next = new Set(prev);
                            if (next.has(key)) next.delete(key); else next.add(key);
                            if (next.size > 0 && (!priorityType || selectedExceptions.size === 0)) setPriority("States");
                            if (next.size === 0 && selectedExceptions.size > 0) setPriority("Exceptions");
                            if (next.size === 0 && selectedExceptions.size === 0) setPriority(null);
                            setTimeout(() => { if (isRestored && filtersLoaded) refreshCounts(); }, 0);
                            return next;
                          });
                        }}>
                          <Checkbox checked={selectedStates.has(key)} size="small" />
                          <ListItemText primary={<span>{`${key} (${value})`}</span>} />
                        </MenuItem>
                      ))}
                    </Menu>
                  </Stack>
                )}
              </Stack>
            </Grid>
          )}
        </Grid>
      )}
    </ThemeProvider>
  );
};

export default HeaderBar;